import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  projectOptimisticReview,
  ReviewProjectionError,
} from "@/components/smart-creation/optimistic-review";
import {
  ReviewEditQueue,
  type ReviewEditQueueOptions,
} from "@/components/smart-creation/review-edit-queue";
import {
  StructureSelectionQueue,
  type StructureSelectionQueueOptions,
} from "@/components/smart-creation/structure-selection-queue";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  updateSmartCreationPlanStructure,
  updateSmartCreationReviewWorkspace,
} from "@/lib/smart-creation-api";
import {
  clearSmartCreationLocalState,
  writeSmartCreationLocalState,
} from "@/lib/smart-creation-local-state";
import { draftsMatch } from "./draft-rules";
import type { PlanSession } from "./use-plan-session";

interface EditQueuesOptions {
  session: PlanSession;
  open: boolean;
  canEdit: boolean;
}

/**
 * Wires the Structure selection and Review edit queues to the Plan session. Both queues
 * coalesce rapid edits into serial compare-and-set writes against the current revision.
 */
export function useEditQueues({ session, open, canEdit }: EditQueuesOptions): void {
  const { t } = useTranslation("smartCreation");
  const {
    actionLock,
    browserDraftRef,
    currentIdentityRef,
    draftRef,
    localChangesRef,
    mountedRef,
    revisionConflictRef,
    reviewQueueRef,
    structureQueueRef,
  } = session;
  const structureQueueOptionsRef = useRef<StructureSelectionQueueOptions | null>(null);
  const reviewQueueOptionsRef = useRef<ReviewEditQueueOptions | null>(null);

  structureQueueOptionsRef.current = {
    getPlan: () => browserDraftRef.current,
    canEdit: () =>
      open &&
      canEdit &&
      (browserDraftRef.current?.status === "draft" ||
        (browserDraftRef.current?.status === "preview_ready" &&
          browserDraftRef.current.draft.currentStep === "structure")) &&
      !actionLock.current &&
      !revisionConflictRef.current,
    canContinue: () =>
      open &&
      canEdit &&
      (browserDraftRef.current?.status === "draft" ||
        (browserDraftRef.current?.status === "preview_ready" &&
          browserDraftRef.current.draft.currentStep === "structure")) &&
      !revisionConflictRef.current,
    save: (current, structure) =>
      updateSmartCreationPlanStructure(current.id, current.shopId, {
        expectedRevision: current.revision,
        structure,
      }),
    commit: (saved, latest) => session.setPlan(saved, latest),
    project: (structure, pending) => {
      session.setStructureSaving(pending);
      const current = browserDraftRef.current;
      if (!current || !mountedRef.current) return;
      const next = structure ? { ...current.draft, structure } : current.draft;
      draftRef.current = next;
      localChangesRef.current = !draftsMatch(next, current.draft);
      session.setDraft(next);
      if (localChangesRef.current) {
        if (!writeSmartCreationLocalState(current.shopId, current.id, current.revision, next))
          toast.warning(t("errors.localStorage"), { id: "smart-creation-storage" });
      } else clearSmartCreationLocalState(current.shopId, current.id);
    },
    failed: async (error, current) => {
      const identity = `${current.shopId}:${current.id}`;
      const conflicted = await session.handleRevisionConflict(error, {
        identity,
        planId: current.id,
        shopId: current.shopId,
      });
      if (!conflicted) {
        const message = await getApiErrorMessage(error);
        if (mountedRef.current && identity === currentIdentityRef.current) toast.error(message);
      }
    },
  };
  if (!structureQueueRef.current)
    structureQueueRef.current = new StructureSelectionQueue(
      () => structureQueueOptionsRef.current!,
    );
  useEffect(() => {
    if (!open) structureQueueRef.current?.reset();
    return () => structureQueueRef.current?.reset();
  }, [open, structureQueueRef]);

  reviewQueueOptionsRef.current = {
    getPlan: () => browserDraftRef.current ?? session.queriedPlan ?? null,
    canEdit: () =>
      open &&
      canEdit &&
      browserDraftRef.current?.status === "preview_ready" &&
      !actionLock.current &&
      !revisionConflictRef.current,
    project: (current, changes) =>
      projectOptimisticReview(current.reviewWorkspace!, changes, session.capabilitiesQuery.data),
    save: (current, changes, clientMutationId) =>
      updateSmartCreationReviewWorkspace(
        current.id,
        current.shopId,
        {
          expectedPlanRevision: current.revision,
          expectedWorkspaceRevision: current.reviewWorkspace!.workspaceRevision,
          clientMutationId,
          changes,
        },
        session.capabilitiesQuery.data,
      ),
    commit: session.setPlan,
    changed: (workspace, pending) => {
      session.setReviewSaving(pending);
      session.setOptimisticReview(
        workspace ? { identity: currentIdentityRef.current, workspace } : null,
      );
    },
    failed: async (error, current) => {
      if (error instanceof ReviewProjectionError) {
        toast.error(t(`errors:smart_creation.${error.code}`));
        return false;
      }
      const conflicted = await session.handleRevisionConflict(error, {
        identity: currentIdentityRef.current,
        planId: current.id,
        shopId: current.shopId,
      });
      if (!conflicted) {
        const message = await getApiErrorMessage(error);
        if (mountedRef.current && `${current.shopId}:${current.id}` === currentIdentityRef.current)
          toast.error(message);
      }
      return conflicted;
    },
  };
  if (!reviewQueueRef.current)
    reviewQueueRef.current = new ReviewEditQueue(() => reviewQueueOptionsRef.current!);
  useEffect(() => () => reviewQueueRef.current?.reset(), [reviewQueueRef]);
}
